<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useStore } from 'vuex';

const props = defineProps({
  chat: { type: Object, default: () => ({}) },
});

const store = useStore();
const pillRef = ref(null);
const openMenu = ref(false);

const CW_LABELS = ['burbujas', 'alquiler', 'proveedor', 'excluir'];

const conversationLabels = computed(() => props.chat?.labels || []);
const activeLabels = computed(() => CW_LABELS.filter(l => conversationLabels.value.includes(l)));
const pillLabel = computed(() => {
  if (activeLabels.value.length === 0) return 'Etiquetas';
  if (activeLabels.value.length === 1) return activeLabels.value[0];
  return `${activeLabels.value.length} etiquetas`;
});

function hasLabel(label) {
  return conversationLabels.value.includes(label);
}

async function toggleLabel(label) {
  const current = [...conversationLabels.value];
  const idx = current.indexOf(label);
  if (idx >= 0) {
    current.splice(idx, 1);
  } else {
    current.push(label);
  }
  await store.dispatch('conversationLabels/update', {
    conversationId: props.chat.id,
    labels: current,
  });
}

function onClickOutside(e) {
  if (pillRef.value && !pillRef.value.contains(e.target)) {
    openMenu.value = false;
  }
}

onMounted(() => document.addEventListener('click', onClickOutside));
onUnmounted(() => document.removeEventListener('click', onClickOutside));
</script>

<template>
  <div ref="pillRef" class="hw-pill-wrap">
    <button
      class="hw-pill"
      :class="{ 'hw-has-val': activeLabels.length > 0 }"
      @click.stop="openMenu = !openMenu"
    >
      <svg class="hw-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>
      <span class="hw-label">{{ pillLabel }}</span>
      <svg class="hw-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>
    </button>
    <div v-if="openMenu" class="hw-dropdown" @click.stop>
      <button
        v-for="label in CW_LABELS"
        :key="label"
        class="hw-dd-item"
        :class="{ active: hasLabel(label) }"
        @click="toggleLabel(label)"
      >
        <span class="hw-check">{{ hasLabel(label) ? '✓' : '' }}</span>
        <span class="hw-dd-label">{{ label }}</span>
      </button>
    </div>
  </div>
</template>

<style scoped>
.hw-pill-wrap {
  position: relative;
}

.hw-pill {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  border: 1px solid rgb(var(--slate-5));
  border-radius: 12px;
  background: transparent;
  color: rgb(var(--slate-10));
  font-size: 11px;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.15s;
  text-transform: capitalize;
}
.hw-pill:hover {
  background: rgb(var(--slate-3));
  color: rgb(var(--slate-12));
}
.hw-has-val {
  color: rgb(var(--blue-11, 49 100 205));
  border-color: rgba(96, 145, 242, 0.4);
}

.hw-icon {
  width: 12px;
  height: 12px;
  flex-shrink: 0;
}
.hw-chevron {
  width: 10px;
  height: 10px;
  flex-shrink: 0;
  opacity: 0.5;
}
.hw-label {
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.hw-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  background: rgb(var(--slate-2));
  border: 1px solid rgb(var(--slate-4));
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  min-width: 140px;
  z-index: 100;
  overflow: hidden;
}

.hw-dd-item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 12px;
  border: none;
  background: none;
  color: rgb(var(--slate-12));
  cursor: pointer;
  font-size: 12px;
  text-align: left;
  text-transform: capitalize;
}
.hw-dd-item:hover {
  background: rgb(var(--slate-3));
}
.hw-dd-item.active {
  color: rgb(var(--blue-11));
  font-weight: 600;
}

.hw-check {
  width: 14px;
  font-size: 11px;
  color: rgb(var(--blue-11, 49 100 205));
}
</style>
